import React, { useCallback, useEffect, useState } from 'react';
import type { Ctx } from '../ctx';
import { ErrorBanner } from '../ui';

/**
 * Le 37 misure ACN dei soggetti importanti, per questa azienda: elenco semplice da spuntare.
 * «Suggerito» viene dall'ultimo piano (controlli e documenti); la spunta la mette Tecnoside, con la prova scritta.
 */
type Suggested = 'da_fare' | 'manca_documento' | 'controlli_ok' | 'documento_pronto' | 'da_verificare';
interface Measure {
  code: string; area: string; title: string; text: string; requirements: string[];
  suggested: Suggested | null; found: string[]; tasks: { n: number; titolo: string }[]; documents: string[];
  nis2Docs?: { code: string; title: string; approvedAt: string | null; version: number | null; reference: string | null }[];
  done: boolean; note: string; doneAt: string | null; doneBy: string | null;
}
interface Data { fonte: string; misure: Measure[] }

const label: Record<Suggested, string> = {
  da_fare: 'Da fare', manca_documento: 'Manca il documento', controlli_ok: 'Controlli a posto', documento_pronto: 'Documento pronto', da_verificare: 'Da verificare',
};
const tone = (m: Measure) => (m.done ? 'ok' : m.suggested === 'da_fare' ? 'bad' : m.suggested === 'manca_documento' ? 'warn' : m.suggested === 'controlli_ok' || m.suggested === 'documento_pronto' ? 'ok' : 'none');
const stateText = (m: Measure) => (m.done ? 'Fatta' : m.suggested ? label[m.suggested] : 'Senza piano');

type Filter = 'all' | 'open' | 'done' | 'da_fare' | 'manca_documento' | 'da_verificare';

export function SecurityAcn({ ctx, siteId }: { ctx: Ctx; siteId: number }) {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('open');
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await ctx.api.get<Data>(`/api/guard/sites/${siteId}/acn`)); setError(null); }
    catch (e) { setError((e as Error).message); }
  }, [ctx.api, siteId]);
  useEffect(() => { void load(); }, [load]);

  const save = async (m: Measure, done: boolean, note: string) => {
    try { setData(await ctx.api.request<Data>('PATCH', `/api/guard/sites/${siteId}/acn/${m.code}`, { done, note })); setError(null); }
    catch (e) { setError((e as Error).message); throw e; }
  };

  if (!data) return <section className="sov-panel" style={{ marginTop: 12 }}><header><b>Misure ACN</b></header><ErrorBanner message={error} /><p className="sov-empty">Caricamento…</p></section>;

  const all = data.misure;
  const done = all.filter((m) => m.done).length;
  const count = (s: Suggested) => all.filter((m) => !m.done && m.suggested === s).length;
  const noPlan = all.every((m) => m.suggested === null);
  const shown = all.filter((m) => filter === 'all' || (filter === 'open' && !m.done) || (filter === 'done' && m.done) ||
    (!m.done && m.suggested === filter) || (filter === 'da_verificare' && !m.done && m.suggested === null));
  const chips: [Filter, string][] = [
    ['open', `Da completare ${all.length - done}`], ['da_fare', `Da fare ${count('da_fare')}`], ['manca_documento', `Manca il documento ${count('manca_documento')}`],
    ['da_verificare', `Da verificare ${count('da_verificare') + all.filter((m) => !m.done && m.suggested === null).length}`], ['done', `Fatte ${done}`], ['all', `Tutte ${all.length}`],
  ];
  const pct = Math.round((done / all.length) * 100);

  return <div className="sov" style={{ marginTop: 12 }}>
    <ErrorBanner message={error} onClose={() => setError(null)} />
    <div className="sov-kpis">
      <div className="sov-k"><span>Misure fatte</span><b>{done}<em> / {all.length}</em></b><div className="sov-bar"><i style={{ width: `${pct}%` }} /></div></div>
      <div className="sov-k"><span>Da fare</span><b className={count('da_fare') ? 'bad' : ''}>{count('da_fare')}</b><small>lavori o problemi aperti</small></div>
      <div className="sov-k"><span>Manca il documento</span><b>{count('manca_documento')}</b><small>da scrivere e far firmare</small></div>
      <div className="sov-k"><span>Requisiti</span><b>{all.reduce((n, m) => n + m.requirements.length, 0)}</b><small>nelle {all.length} misure</small></div>
    </div>
    {noPlan && <p className="sov-empty" style={{ padding: 0 }}>Gli stati «Da fare» e «Manca il documento» arrivano dal piano: crealo o aggiornalo nella Guida.</p>}
    <section className="sov-panel">
      <header><b>Misure ACN per i soggetti importanti</b><span>spunta quando una misura è fatta, con la prova</span></header>
      <div className="sov-chips" role="tablist" aria-label="Filtra le misure">
        {chips.map(([id, text]) => <button key={id} type="button" role="tab" aria-selected={filter === id} className={`sov-chip${filter === id ? ' on' : ''}`} onClick={() => setFilter(id)}>{text}</button>)}
      </div>
      {shown.length === 0 ? <p className="sov-empty">Nessuna misura in questo elenco.</p> : shown.map((m) =>
        <AcnRow key={m.code} m={m} open={open === m.code} onToggle={() => setOpen(open === m.code ? null : m.code)} onSave={save} />)}
      <footer><span className="muted small">Fonte: {data.fonte || 'Determinazione ACN 379907/2025, Allegato 1'}. Lo stato suggerito viene dai controlli e dal piano: non è una dichiarazione di conformità.</span></footer>
    </section>
  </div>;
}

function AcnRow({ m, open, onToggle, onSave }: { m: Measure; open: boolean; onToggle: () => void; onSave: (m: Measure, done: boolean, note: string) => Promise<void> }) {
  const [note, setNote] = useState(m.note);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(m.note); }, [m.note]);
  const submit = async (done: boolean) => {
    setBusy(true);
    try { await onSave(m, done, note.trim()); } catch { /* errore già mostrato */ } finally { setBusy(false); }
  };
  return <div className="acn-row">
    <button type="button" className="acn-line" aria-expanded={open} onClick={onToggle}>
      <span className={`acn-box${m.done ? ' on' : ''}`} aria-hidden="true">{m.done ? '✓' : ''}</span>
      <span className="acn-code">{m.code}</span>
      <span className="acn-title">{m.title}<small>{m.area} · {m.requirements.length} {m.requirements.length === 1 ? 'requisito' : 'requisiti'}</small></span>
      <span className={`sov-st ${tone(m)}`}>{stateText(m)}</span>
    </button>
    {open && <div className="acn-body">
      <p style={{ margin: '0 0 6px' }}><b>Cosa chiede l’ACN.</b> {m.text}</p>
      <ol className="acn-req">{m.requirements.map((r, i) => <li key={i}>{r}</li>)}</ol>
      {m.found.length > 0 && <><b className="small">Trovato dai controlli</b><ul className="acn-list">{m.found.map((f, i) => <li key={i}>{f}</li>)}</ul></>}
      {m.tasks.length > 0 && <><b className="small">Lavori del piano</b><ul className="acn-list">{m.tasks.map((t) => <li key={t.n}>{t.n}. {t.titolo}</li>)}</ul></>}
      {m.documents.length > 0 && <><b className="small">Documenti</b><ul className="acn-list">{m.documents.map((d, i) => <li key={i}>{d}</li>)}</ul></>}
      {(m.nis2Docs?.length ?? 0) > 0 && <><b className="small">Documenti NIS2 di Sentinel</b><ul className="acn-list">{m.nis2Docs!.map((d) => <li key={d.code}>{d.code} {d.title} · {d.approvedAt ? `approvato v${d.version} il ${new Date(d.approvedAt).toLocaleDateString('it-IT')}` : 'non ancora approvato'}</li>)}</ul></>}
      <label className="small" style={{ display: 'block', marginTop: 10 }}>Prova (cosa è stato fatto e dove si trova)
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={4000} style={{ width: '100%', marginTop: 4 }} placeholder="Es. BitLocker attivo su 12 PC, report Assessment del 30/10 nel fascicolo" />
      </label>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {m.done
          ? <button type="button" className="sov-btn" disabled={busy} onClick={() => void submit(false)}>Togli la spunta</button>
          : <button type="button" className="sov-btn primary" disabled={busy || note.trim().length < 5} title={note.trim().length < 5 ? 'Scrivi prima la prova' : 'Segna la misura come fatta'} onClick={() => void submit(true)}>Segna come fatta</button>}
        {note !== m.note && <button type="button" className="sov-btn" disabled={busy} onClick={() => void submit(m.done)}>Salva la nota</button>}
        {m.done && m.doneAt && <span className="muted small">Fatta il {new Date(m.doneAt).toLocaleDateString('it-IT')}{m.doneBy ? ` da ${m.doneBy}` : ''}</span>}
      </div>
    </div>}
  </div>;
}
